import { useEffect, useState } from 'react';
import { Typography } from '../typography';
import { Button } from '../button';
import { Input, InputPrice } from '../input';
import { BorderLine } from '../border-line';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { enqueueSnackbar } from '../snackbar';
import { useStore } from '../../context';
import { useSalesFeeForm, useSalesFeeUpdateMutation } from '../../hooks';
import { formatContainerType, momentTextFormat } from '../../utils';
import { saveCache, getCache } from '../../services';
import { containerTypes, INPUT_ERRORS, MESSAGES, SALES_FEE, SalesFee } from '../../const';
import { FeeType, SalesFeeUpdateModalCache } from '../../types';
import { CheckboxLabel } from '../checkbox';
import { i18n } from '../../i18n';

export const SalesFeeUpdateModal = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();
  const { mutate } = useSalesFeeUpdateMutation();

  const [open, setOpen] = useState(false);
  const [yardName, setYardName] = useState('');
  const [isPercentageFee, setIsPercentageFee] = useState(false);
  const [{ effectiveDate, ...containers }, setFeeState] = useSalesFeeForm({
    effectiveDate: '',
  });
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);

  const validators = {
    ...Object.fromEntries(
      containerTypes.map((containerType) => [
        containerType.value,
        String(containers[containerType.value]).length !== 0,
      ]),
    ),
  };

  useEffect(() => {
    const open = () => {
      const cachedData = getCache<SalesFeeUpdateModalCache>('sales-fee-update-modal');
      const { containers, ...feeRecord } = cachedData.activeFee;

      setYardName(cachedData.yard.name);
      setIsPercentageFee(feeRecord.feeType === FeeType.PERCENTAGE);
      setFeeState({
        ...SALES_FEE,
        effectiveDate: momentTextFormat(feeRecord.activationDate, user),
        ...containers.reduce(
          (prev, curr) => ({
            ...prev,
            [curr.type]: curr.fee,
          }),
          {},
        ),
      } as SalesFee);

      setIsSubmitClicked(false);
      setOpen(true);
    };

    saveCache<SalesFeeUpdateModalCache>('sales-fee-update-modal', (props) => ({
      ...props,
      open,
    }));
  }, []);

  const handleOnClose = () => {
    const openModalFc = getCache<SalesFeeUpdateModalCache>('sales-fee-update-modal').open;
    saveCache('sales-fee-update-modal', { open: openModalFc });
    setOpen(!open);
  };

  const handleSubmitClick = async () => {
    setIsSubmitClicked(true);
    if ([...Object.values(validators)].some((val) => !val)) {
      return;
    }

    const formProps = {
      id: getCache<SalesFeeUpdateModalCache>('sales-fee-update-modal').id,
      activeFeeRecordId: getCache<SalesFeeUpdateModalCache>('sales-fee-update-modal').activeFee.id,
      feeType: isPercentageFee ? FeeType.PERCENTAGE : FeeType.FIXED,
      ...containers,
    };

    mutate(formProps, {
      onSuccess: () => {
        enqueueSnackbar(MESSAGES.SALES_FEE.SUCCESS.UPDATED, 'success');
        setOpen(false);
        getCache<SalesFeeUpdateModalCache>('sales-fee-update-modal').refetch();
      },
    });
  };

  return (
    <ModalBase
      id={'sales-fee-update-modal'}
      open={open}
      onClose={handleOnClose}
      paperSize={'very-big'}
    >
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.salesFeeUpdateTitle')}
        </Typography>
        <ModalBodyCustomColumns columns={'1fr 1fr'}>
          <Input
            label={i18n.t('common.yard')}
            data-size={'medium'}
            defaultValue={yardName}
            readOnly
          />
          <Input
            label={i18n.t('label.effectiveDate')}
            data-size={'medium'}
            defaultValue={effectiveDate}
            readOnly
          />
          <CheckboxLabel
            label={i18n.t('label.percentageFee')}
            checked={isPercentageFee}
            onChange={(e) => setIsPercentageFee(e.target.checked)}
          />
        </ModalBodyCustomColumns>
        <BorderLine space={'dashboard-form'} />
        {Array.from({ length: Math.ceil(containerTypes.length / 2) }, (_, rowIndex) => (
          <ModalBodyCustomColumns key={`container-row-${rowIndex}`} columns={'1fr 1fr'}>
            {containerTypes.slice(rowIndex * 2, rowIndex * 2 + 2).map(({ value: key }) => (
              <InputPrice
                key={key}
                label={formatContainerType(key)}
                data-size={'medium'}
                defaultValue={containers[key]}
                onChange={(e) => {
                  setFeeState((props) => {
                    props[key] = Number(e.target.value);
                  });
                }}
                isPercent={isPercentageFee}
                error={isSubmitClicked ? !String(containers[key]).length : false}
                helperText={
                  isSubmitClicked
                    ? !String(containers[key]).length
                      ? INPUT_ERRORS.REQUIRED.FEE
                      : ''
                    : ''
                }
              />
            ))}
          </ModalBodyCustomColumns>
        ))}
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
            {i18n.t('button.update')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
